import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  AlignLeft, Archive, CalendarClock, CalendarPlus, ChevronDown, ChevronRight, ChevronUp, CircleCheck, Copy, Ellipsis, Eye, EyeOff, File as FileIcon, Flag, GitBranch,
  Hourglass, Link2, ListChecks, Maximize2, Minimize2, PanelRightOpen, Paperclip, Plus, SlidersHorizontal, Tag as TagIcon, Trash2, Upload, Users, X,
} from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import type { CustomField, MissionDetail, Status } from '@missions/types';
import { Button, DatePicker, Select, useDialogs, useToast, useUnsavedChanges } from '@missions/ui';
import { Layer } from '../../components/Layer';
import { Menu, type MenuItem } from '../../components/Menu';
import { Av, Radar, SDot, fmtDate } from '../../components/atoms';
import { api } from '../../lib/client';
import { useApi, useErrorToast, useStoredFlag } from '../../lib/hooks';
import { useSession, useT } from '../../lib/session';
import { ActivityColumn } from './ActivityColumn';
import { celebrate, priorityOptions, statusOptions, useMemberOptions, useMissionInvalidate, useUpdateMission } from './data';

const fmtBytes = (n: number) => (n > 1e6 ? `${(n / 1e6).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1e3))} KB`);

/** Mission detail panel (mockups/app.html #panel), opened anywhere via `?m=<missionId>`. */
export function MissionPanel() {
  const [sp, setSp] = useSearchParams();
  const id = sp.get('m');
  const [wide, setWide] = useState(false);
  const [actHidden, setActHidden] = useStoredFlag('ms-act-hidden', false);
  const [pt, setPt] = useState<'d' | 'a'>('d');
  useEffect(() => setPt('d'), [id]);
  const close = () => {
    const next = new URLSearchParams(sp);
    next.delete('m');
    setSp(next);
  };
  const open = (nid: string) => {
    const n = new URLSearchParams(sp);
    n.set('m', nid);
    setSp(n);
  };
  return (
    <Layer
      open={!!id}
      onRequestClose={close}
      className={`panel${wide ? ' p-wide' : ''}${actHidden ? ' act-hidden' : ''}`}
      as="aside"
      label="Mission details"
      data={{ pt }}
    >
      {id && <PanelBody id={id} onClose={close} onOpen={open} wide={wide} setWide={setWide} actHidden={actHidden} setActHidden={setActHidden} pt={pt} setPt={setPt} />}
    </Layer>
  );
}

function PanelBody({ id, ...rest }: Omit<Props, 'm'> & { id: string }) {
  const q = useApi('missions.get', { params: { id } });
  const t = useT();
  if (q.isPending)
    return (
      <div className="p-load"><Radar size={56} /><span>Loading {t.item}…</span></div>
    );
  if (q.isError || !q.data)
    return (
      <div className="p-load">
        <h3 style={{ margin: 0 }}>This {t.item} was deleted or moved</h3>
        <Button onClick={rest.onClose}>Close</Button>
      </div>
    );
  return <PanelLoaded key={q.data.id} m={q.data} {...rest} />;
}

interface Props {
  m: MissionDetail;
  wide: boolean;
  setWide: (v: boolean) => void;
  actHidden: boolean;
  setActHidden: (v: boolean) => void;
  pt: 'd' | 'a';
  setPt: (v: 'd' | 'a') => void;
  onClose: () => void;
  onOpen: (id: string) => void;
}

function PanelLoaded({ m, wide, setWide, actHidden, setActHidden, pt, setPt, onClose, onOpen }: Props) {
  const s = useSession();
  const t = useT();
  const toast = useToast();
  const dialogs = useDialogs();
  const onErr = useErrorToast();
  const qc = useQueryClient();
  const refresh = useMissionInvalidate();
  const update = useUpdateMission();
  const members = useMemberOptions();
  const project = useApi('projects.get', { params: { id: m.projectId } });
  const statuses: Status[] = project.data?.statuses ?? [m.status];
  const fields = useApi('customFields.list', { query: { workspaceId: project.data?.workspaceId ?? '', projectId: m.projectId } }, { enabled: !!project.data });
  const tags = useApi('tags.list', { query: { workspaceId: project.data?.workspaceId ?? '' } }, { enabled: !!project.data });
  const ro = !s.can('mission.edit');
  const moreRef = useRef<HTMLButtonElement>(null);
  const [more, setMore] = useState(false);
  const completeRef = useRef<HTMLButtonElement>(null);

  // text fields are drafts (unsaved bar + guard); discrete fields save immediately
  const [title, setTitle] = useState(m.title);
  const [desc, setDesc] = useState(m.description ?? '');
  const [eta, setEta] = useState(m.etaMinutes == null ? '' : String(Math.round((m.etaMinutes / 60) * 10) / 10));
  const etaOrig = m.etaMinutes == null ? '' : String(Math.round((m.etaMinutes / 60) * 10) / 10);
  const dirty = title.trim() !== m.title || desc !== (m.description ?? '') || eta !== etaOrig;
  const saveText = async () => {
    if (!title.trim()) {
      setTitle(m.title);
      toast(`${t.Item} name can’t be empty. Restored the previous name.`, { type: 'error' });
      return;
    }
    await update.mutateAsync({ id: m.id, patch: { title: title.trim(), description: desc, etaMinutes: eta ? Math.round(parseFloat(eta) * 60) : null } });
    toast('Changes saved');
  };
  useUnsavedChanges(dirty, {
    bar: true,
    onSave: saveText,
    onDiscard: () => {
      setTitle(m.title);
      setDesc(m.description ?? '');
      setEta(etaOrig);
    },
  });

  const patch = (p: Parameters<typeof update.mutate>[0]['patch']) =>
    update.mutate(
      { id: m.id, patch: p },
      {
        onSuccess: (nm) => {
          qc.setQueryData<typeof nm>(['missions', 'missions.get', { id: m.id }, null], nm);
        },
      },
    );

  const closedStatus = statuses.find((x) => x.isClosed);
  const complete = async () => {
    if (!closedStatus) return toast('This project has no “done” status yet.', { type: 'info' });
    const prev = m.statusId;
    try {
      await api.call('missions.update', { params: { id: m.id }, body: { statusId: closedStatus.id } });
      if (completeRef.current) celebrate(completeRef.current);
      await refresh();
      toast(`${t.itemCompleted}: ${m.title}`, {
        action: { label: 'Undo', onClick: () => void api.call('missions.update', { params: { id: m.id }, body: { statusId: prev } }).then(() => refresh()) },
      });
    } catch (e) {
      onErr(e);
    }
  };

  const remove = async () => {
    if (!(await dialogs.confirm({ tone: 'danger', title: `Delete this ${t.item}?`, message: <><b>“{m.title}”</b> and its {m.objectives.length} {t.subItems} will be moved to Trash. You can restore it within 30 days.</>, confirmText: `Delete ${t.item}` }))) return;
    try {
      const r = await api.call('missions.delete', { params: { id: m.id } });
      onClose();
      await refresh();
      toast(`${t.Item} deleted`, { action: { label: 'Undo', onClick: () => void api.call('missions.undo', { body: { undoToken: r.undoToken } }).then(() => refresh()) } });
    } catch (e) {
      onErr(e);
    }
  };
  const copyLink = () => {
    void navigator.clipboard?.writeText(`${location.origin}${location.pathname}?m=${m.id}`);
    toast(`${t.Item} link copied`);
  };
  const moreItems: MenuItem[] = [
    { label: 'Copy link', icon: <Link2 />, onClick: copyLink },
    {
      label: 'Duplicate',
      icon: <Copy />,
      disabled: !s.can('mission.create'),
      lock: 'Member',
      onClick: () =>
        void api
          .call('missions.duplicate', { params: { id: m.id } })
          .then((d) => {
            void refresh();
            toast(`Duplicated as ${d.key}`, { action: { label: 'Open', onClick: () => onOpen(d.id) } });
          })
          .catch(onErr),
    },
    {
      label: `Archive ${t.item}`,
      icon: <Archive />,
      disabled: ro,
      lock: 'Member',
      onClick: async () => {
        if (await dialogs.confirm({ tone: 'warning', title: `Archive this ${t.item}?`, message: `Archived ${t.items} are hidden from lists and boards but stay searchable.`, confirmText: 'Archive' })) {
          const done = statuses.find((x) => x.isClosed);
          if (done) patch({ statusId: done.id });
          onClose();
          toast(`${t.Item} archived`);
        }
      },
    },
    '-',
    { label: `Delete ${t.item}`, icon: <Trash2 />, danger: true, disabled: !s.can('mission.delete'), lock: 'Manager', onClick: () => void remove() },
  ];

  const isClosed = !!statuses.find((x) => x.id === m.statusId)?.isClosed;
  const tagOptions = (tags.data?.data ?? []).map((g) => ({ value: g.id, label: g.name, color: g.color }));

  return (
    <>
      <div className="p-top">
        <div className="p-crumb">
          <span className="dot" style={{ background: m.project.color }} />
          <span className="pc-ctx">{m.project.name}</span>
          <ChevronRight />
          <span>{m.status.name}</span>
          <ChevronRight />
          <span className="ms-code">{t.Item} {m.key}</span>
        </div>
        <button className="ib" disabled={!m.neighbours.prevId} onClick={() => m.neighbours.prevId && onOpen(m.neighbours.prevId)} aria-label={`Previous ${t.item}`} data-tooltip={`Previous ${t.item}`} data-shortcut="K"><ChevronUp /></button>
        <button className="ib" disabled={!m.neighbours.nextId} onClick={() => m.neighbours.nextId && onOpen(m.neighbours.nextId)} aria-label={`Next ${t.item}`} data-tooltip={`Next ${t.item}`} data-shortcut="J"><ChevronDown /></button>
        <button className="ib p-wide-btn" aria-pressed={wide} onClick={() => setWide(!wide)} aria-label={wide ? 'Exit full width' : 'Expand to full width'} data-tooltip={wide ? 'Exit full width' : 'Expand to full width'}>{wide ? <Minimize2 /> : <Maximize2 />}</button>
        <button className="ib" onClick={copyLink} aria-label={`Copy ${t.item} link`} data-tooltip={`Copy ${t.item} link`}><Link2 /></button>
        <button
          className="ib"
          aria-pressed={m.watching}
          onClick={() =>
            void api
              .call('missions.watch', { params: { id: m.id }, body: { watching: !m.watching } })
              .then(() => {
                void refresh();
                toast(!m.watching ? `You’ll be notified about updates to this ${t.item}` : `You won’t get notifications for this ${t.item} anymore`, { type: 'info' });
              })
              .catch(onErr)
          }
          aria-label={m.watching ? 'Watching · click to stop' : 'Not watching · click to watch'}
          data-tooltip={m.watching ? 'Watching · click to stop getting updates' : 'Not watching · click to get updates'}
        >
          {m.watching ? <Eye /> : <EyeOff />}
        </button>
        <button className="ib" ref={moreRef} onClick={() => setMore((o) => !o)} aria-label={`More ${t.item} actions`} data-tooltip={`More ${t.item} actions`}><Ellipsis /></button>
        <Menu anchorRef={moreRef} open={more} onClose={() => setMore(false)} title={m.title} items={moreItems} alignRight />
        <button className="ib" onClick={onClose} aria-label={`Close ${t.item}`} data-tooltip="Close" data-shortcut="Esc"><X /></button>
      </div>
      <div className="p-seg" role="tablist" aria-label={`${t.Item} sections`}>
        <button type="button" role="tab" aria-selected={pt === 'd'} onClick={() => setPt('d')}>Details</button>
        <button type="button" role="tab" aria-selected={pt === 'a'} onClick={() => setPt('a')}>Activity <span className="n">{m.counts.comments}</span></button>
      </div>
      <div className="p-body">
        <div className="p-main">
          {ro && <div className="ro-note"><Eye />You have view & comment access to this project.</div>}
          <div className="p-status-row">
            <Select size="sm" value={m.statusId} onChange={(v) => v && patch({ statusId: v })} options={statusOptions(statuses)} aria-label="Status" title="Status" disabled={ro} />
            {!isClosed && (
              <button type="button" ref={completeRef} className="btn btn-o sm" disabled={ro} onClick={() => void complete()}>
                <CircleCheck style={{ color: 'var(--done)' }} />Mark complete
              </button>
            )}
            <span className="p-id">
              <button
                type="button"
                className="ms-code p-idbtn"
                onClick={() => {
                  void navigator.clipboard?.writeText(m.key);
                  toast(`${t.itemId} ${m.key} copied`);
                }}
                aria-label={`Copy ${t.itemId} ${m.key}`}
                data-tooltip={`Copy ${t.itemId}`}
              >
                {m.key}
              </button>{' '}
              · Created {fmtDate(m.createdAt.slice(0, 10))} by {m.createdBy.name}
            </span>
          </div>
          <input
            className="p-title"
            aria-label={`${t.Item} title`}
            value={title}
            readOnly={ro}
            onChange={(e) => setTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                void saveText();
              }
            }}
          />
          <div className="props">
            <Prop icon={<Users />} label="Assignees">
              <Select multiple bare value={m.assignees.map((a) => a.id)} onChange={(v) => patch({ assigneeIds: v })} options={members.options} searchable placeholder="Add assignees" aria-label="Assignees" title="Assignees" disabled={ro} />
            </Prop>
            <Prop icon={<Flag />} label="Priority">
              <Select bare value={m.priority} onChange={(v) => v && patch({ priority: v })} options={priorityOptions()} aria-label="Priority" title="Priority" disabled={ro} />
            </Prop>
            <Prop icon={<CalendarPlus />} label="Start date">
              <DatePicker bare value={m.startAt} onChange={(v) => patch({ startAt: v })} placeholder="Add start date" aria-label="Start date" disabled={ro} />
            </Prop>
            <Prop icon={<CalendarClock />} label="Due date">
              <DatePicker bare due value={m.dueAt} onChange={(v) => patch({ dueAt: v })} placeholder="Add due date" aria-label="Due date" disabled={ro} />
            </Prop>
            <div className="prop">
              <label className="k" htmlFor="fEta"><Hourglass />ETA</label>
              <div className="eta-in">
                <input className="ui-input" id="fEta" inputMode="decimal" value={eta} placeholder="0" readOnly={ro} onChange={(e) => setEta(e.target.value.replace(/[^\d.]/g, ''))} data-tooltip="Time estimate (ETA)" />
                <span>hrs</span>
              </div>
            </div>
            <Prop icon={<TagIcon />} label="Tags">
              <Select multiple bare value={m.tags.map((g) => g.id)} onChange={(v) => patch({ tagIds: v })} options={tagOptions} placeholder="Add tags" aria-label="Tags" title="Tags" disabled={ro} />
            </Prop>
          </div>

          <CustomFields fields={fields.data?.data ?? []} m={m} ro={ro} onSet={(fieldId, value) => void api.call('missions.setCustomValue', { params: { id: m.id, fieldId }, body: { value } }).then(() => refresh()).catch(onErr)} />

          <div className="sec">
            <div className="sec-h"><AlignLeft />Description</div>
            <div className="rte">
              <textarea className="rte-c" aria-label="Description" value={desc} readOnly={ro} placeholder="Add details, links and acceptance notes…" onChange={(e) => setDesc(e.target.value)} />
            </div>
          </div>
          <Objectives m={m} statuses={statuses} ro={ro} onOpen={onOpen} />
          <Checklist m={m} ro={ro} />
          <Attachments m={m} ro={ro} />
        </div>
        {!actHidden && <ActivityColumn mission={m} onHide={() => setActHidden(true)} />}
      </div>
      {actHidden && (
        <button type="button" className="act-tab" onClick={() => setActHidden(false)} aria-label="Show activity" data-tooltip="Show activity">
          <PanelRightOpen />
          <span>Activity · <b>{m.counts.comments}</b></span>
        </button>
      )}
    </>
  );
}

const Prop = ({ icon, label, children }: { icon: ReactNode; label: string; children: ReactNode }) => (
  <div className="prop">
    <span className="k">{icon}{label}</span>
    {children}
  </div>
);

function CustomFields({ fields, m, ro, onSet }: { fields: CustomField[]; m: MissionDetail; ro: boolean; onSet: (fieldId: string, v: string | number | boolean | string[] | null) => void }) {
  const s = useSession();
  if (!fields.length && !s.can('field.manage')) return null;
  return (
    <div className="sec">
      <div className="sec-h"><SlidersHorizontal />Custom fields<span className="c">{fields.length} fields</span></div>
      <div className="cf-box">
        {fields.length === 0 && <div style={{ padding: 14, color: 'var(--text-3)' }}>No custom fields yet. Add them in Settings → Custom fields.</div>}
        {fields.map((f) => {
          const v = m.customValues[f.id] ?? null;
          let control: ReactNode;
          if (f.type === 'dropdown' || f.type === 'labels') {
            const opts = f.options.map((o) => ({ value: o.id, label: o.label, color: o.color }));
            control =
              f.type === 'labels' ? (
                <Select multiple bare value={Array.isArray(v) ? v : []} onChange={(x) => onSet(f.id, x)} options={opts} placeholder="Select" aria-label={f.name} disabled={ro} />
              ) : (
                <Select bare clearable value={typeof v === 'string' ? v : null} onChange={(x) => onSet(f.id, x)} options={opts} placeholder="Select" aria-label={f.name} disabled={ro} />
              );
          } else if (f.type === 'checkbox') {
            control = (
              <label className="ck">
                <input type="checkbox" checked={v === true} disabled={ro} onChange={(e) => onSet(f.id, e.target.checked)} aria-label={f.name} />
                <span>{v === true ? 'Yes' : 'No'}</span>
              </label>
            );
          } else if (f.type === 'date') {
            control = <DatePicker bare value={typeof v === 'string' ? v : null} onChange={(x) => onSet(f.id, x)} placeholder="Pick a date" aria-label={f.name} disabled={ro} />;
          } else {
            const numeric = ['number', 'currency', 'progress', 'rating'].includes(f.type);
            control = (
              <input
                className="ui-input"
                defaultValue={v == null ? '' : String(v)}
                inputMode={numeric ? 'decimal' : undefined}
                placeholder="—"
                readOnly={ro}
                aria-label={f.name}
                onBlur={(e) => {
                  const raw = e.target.value.trim();
                  const val = raw === '' ? null : numeric ? Number(raw) : raw;
                  if (val !== v) onSet(f.id, val);
                }}
              />
            );
          }
          return (
            <div className="cf-row" key={f.id}>
              <span className="k"><SlidersHorizontal /><span className="ui-truncate">{f.name}</span></span>
              <div className="v">{control}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Objectives({ m, statuses, ro, onOpen }: { m: MissionDetail; statuses: Status[]; ro: boolean; onOpen: (id: string) => void }) {
  const t = useT();
  const dialogs = useDialogs();
  const toast = useToast();
  const onErr = useErrorToast();
  const refresh = useMissionInvalidate();
  const closed = statuses.find((x) => x.isClosed);
  const open = [...statuses].sort((a, b) => a.sortOrder - b.sortOrder).find((x) => !x.isClosed);
  const done = m.objectives.filter((o) => o.completedAt).length;
  const add = async () => {
    const v = await dialogs.prompt({ title: t.addSubItem, label: `${t.subItem[0]!.toUpperCase()}${t.subItem.slice(1)} name`, placeholder: 'e.g. Export hero images for retina', confirmText: t.addSubItem, validate: (x) => (x.length > 120 ? 'Keep it under 120 characters' : null) });
    if (!v) return;
    try {
      await api.call('missions.create', { body: { projectId: m.projectId, parentId: m.id, title: v } });
      await refresh();
      toast(`${t.subItem[0]!.toUpperCase()}${t.subItem.slice(1)} added`);
    } catch (e) {
      onErr(e);
    }
  };
  return (
    <div className="sec">
      <div className="sec-h">
        <GitBranch />
        {t.subItems[0]!.toUpperCase() + t.subItems.slice(1)}
        <span className="c">{done} of {m.objectives.length} done</span>
        <span className="pbar" style={{ maxWidth: 120, marginLeft: 6 }}><i style={{ width: `${m.objectives.length ? (done / m.objectives.length) * 100 : 0}%` }} /></span>
        {!ro && <button type="button" className="btn btn-g sm act" onClick={() => void add()}><Plus />Add</button>}
      </div>
      {m.objectives.length ? (
        <div className="subs">
          {m.objectives.map((o) => (
            <div className={`subt${o.completedAt ? ' d' : ''}`} key={o.id}>
              <button
                className="sbtn"
                disabled={ro}
                aria-label={o.completedAt ? 'Mark as not done' : 'Mark as done'}
                onClick={(e) => {
                  const target = o.completedAt ? open : closed;
                  if (!target) return;
                  if (!o.completedAt) celebrate(e.currentTarget);
                  void api.call('missions.update', { params: { id: o.id }, body: { statusId: target.id } }).then(() => refresh()).catch(onErr);
                }}
              >
                <SDot s={o.completedAt ? { name: 'Done', color: '#16a34a', isClosed: true } : { name: 'Open', color: '#8a94a6', isClosed: false }} />
              </button>
              <button type="button" className="nm" style={{ textAlign: 'left' }} onClick={() => onOpen(o.id)}>{o.title}</button>
              {o.assignees[0] && <Av u={o.assignees[0]} size={22} />}
              <span className="due">{o.dueAt ? fmtDate(o.dueAt) : '—'}</span>
              {!ro && (
                <button
                  className="x"
                  aria-label={`Delete ${t.subItem}`}
                  onClick={async () => {
                    if (await dialogs.confirm({ tone: 'danger', title: `Delete ${t.subItem}?`, message: <><b>“{o.title}”</b> will be deleted.</>, confirmText: `Delete ${t.subItem}` })) {
                      try {
                        const r = await api.call('missions.delete', { params: { id: o.id } });
                        await refresh();
                        toast(`${t.subItem[0]!.toUpperCase()}${t.subItem.slice(1)} deleted`, { action: { label: 'Undo', onClick: () => void api.call('missions.undo', { body: { undoToken: r.undoToken } }).then(() => refresh()) } });
                      } catch (e) {
                        onErr(e);
                      }
                    }
                  }}
                >
                  <X />
                </button>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div style={{ color: 'var(--text-3)', fontSize: '0.8125rem' }}>No {t.subItems} yet. Break bigger work into smaller steps.</div>
      )}
    </div>
  );
}

function Checklist({ m, ro }: { m: MissionDetail; ro: boolean }) {
  const dialogs = useDialogs();
  const onErr = useErrorToast();
  const refresh = useMissionInvalidate();
  const [items, setItems] = useState(m.checklist);
  useEffect(() => setItems(m.checklist), [m.checklist]);
  const doneN = items.filter((c) => c.done).length;
  return (
    <div className="sec">
      <div className="sec-h">
        <ListChecks />Checklist<span className="c">{doneN}/{items.length}</span>
        {!ro && (
          <button
            type="button"
            className="btn btn-g sm act"
            onClick={async () => {
              const v = await dialogs.prompt({ title: 'Add checklist item', label: 'Item', placeholder: 'e.g. Alt text on images', confirmText: 'Add item', validate: (x) => (x.length > 300 ? 'Keep it under 300 characters' : null) });
              if (v) void api.call('checklist.create', { params: { id: m.id }, body: { text: v } }).then(() => refresh()).catch(onErr);
            }}
          >
            <Plus />Add
          </button>
        )}
      </div>
      {items.map((c) => (
        <label className="check" key={c.id}>
          <input
            type="checkbox"
            checked={c.done}
            disabled={ro}
            onChange={(e) => {
              const done = e.target.checked;
              setItems((xs) => xs.map((x) => (x.id === c.id ? { ...x, done } : x)));
              void api.call('checklist.update', { params: { id: c.id }, body: { done } }).catch((er) => {
                setItems((xs) => xs.map((x) => (x.id === c.id ? { ...x, done: !done } : x)));
                onErr(er);
              });
            }}
          />
          <span>{c.text}</span>
        </label>
      ))}
      {!items.length && <div style={{ color: 'var(--text-3)', fontSize: '0.8125rem' }}>No checklist items.</div>}
    </div>
  );
}

function Attachments({ m, ro }: { m: MissionDetail; ro: boolean }) {
  const s = useSession();
  const dialogs = useDialogs();
  const toast = useToast();
  const onErr = useErrorToast();
  const refresh = useMissionInvalidate();
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState<string | null>(null);
  const maxMb = s.me?.entitlements.limits.max_file_mb ?? 25;
  const upload = async (f: File) => {
    if (f.size > maxMb * 1e6) return toast(`${f.name} is larger than ${maxMb} MB, the limit on your plan.`, { type: 'error' });
    setUploading(f.name);
    try {
      const p = await api.call('uploads.presign', { body: { purpose: 'attachment', fileName: f.name, mimeType: f.type || 'application/octet-stream', sizeBytes: f.size } });
      const res = await fetch(p.url, { method: p.method, headers: p.headers, body: f });
      if (!res.ok) throw new Error('upload failed');
      await api.call('uploads.complete', { body: { uploadId: p.uploadId } });
      await api.call('attachments.add', { params: { id: m.id }, body: { uploadId: p.uploadId, fileName: f.name } });
      await refresh();
      toast(`${f.name} attached`);
    } catch (e) {
      onErr(e, `Couldn’t upload ${f.name}. Try again.`);
    } finally {
      setUploading(null);
    }
  };
  const canUpload = !ro && s.can('attachment.upload');
  const list = useMemo(() => m.attachments, [m.attachments]);
  return (
    <div className="sec">
      <div className="sec-h"><Paperclip />Attachments<span className="c">{list.length} files</span></div>
      <div className="files">
        {list.map((a) => (
          <div className="file" key={a.id}>
            <span className="ic" style={{ background: 'color-mix(in srgb,#0457fd 14%,var(--surface))', color: '#0457fd' }}><FileIcon /></span>
            <div>
              <a href={a.url} target="_blank" rel="noopener noreferrer"><b data-tooltip={a.fileName}>{a.fileName}</b></a>
              <span>{fmtBytes(a.sizeBytes)}</span>
            </div>
            {canUpload && (
              <button
                className="x"
                aria-label={`Remove ${a.fileName}`}
                onClick={async () => {
                  if (await dialogs.confirm({ tone: 'danger', title: 'Remove attachment?', message: <><b>{a.fileName}</b> will be removed from this mission for everyone.</>, confirmText: 'Remove' })) {
                    void api.call('attachments.delete', { params: { id: a.id } }).then(() => { void refresh(); toast('Attachment removed'); }).catch(onErr);
                  }
                }}
              >
                <X />
              </button>
            )}
          </div>
        ))}
        {canUpload && (
          <button
            type="button"
            className="drop"
            onClick={() => fileRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              const f = e.dataTransfer.files[0];
              if (f) void upload(f);
            }}
            disabled={!!uploading}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              {uploading ? <><Radar size={16} />Uploading {uploading}…</> : <><Upload />Drop files or browse</>}
            </span>
          </button>
        )}
        <input
          ref={fileRef}
          type="file"
          className="sr"
          tabIndex={-1}
          aria-hidden="true"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void upload(f);
            e.target.value = '';
          }}
        />
      </div>
    </div>
  );
}
